import {Cell} from 'notebook/tables/constants/CellConstants';

import {StructuredTable} from './StructuredTableConstants';

/** Function arguments for use with iterable functions. This should feel like the arguments used in Array.map, .some, etc.  */
type CellIterationArguments<TTableMetadata, TRowMetadata> = [
  cell: Cell,
  rowIndex: number,
  columnIndex: number,
  table: StructuredTable<TTableMetadata, TRowMetadata>,
];

/**
 * A collection of functions and utilities to be used on Structured Tables to make easy edits.
 *
 * WARNING! Many of these are mutative functions! Therefore, you should only use these operations within an immutable
 * context such as an immer produce for safety!
 */
const StructuredTableHelper = {
  forCellInRow: <TTableMetadata, TRowMetadata>(
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    rowIndex: number,
    fn: (...args: CellIterationArguments<TTableMetadata, TRowMetadata>) => void
  ): void => {
    for (const [columnIndex, cell] of table.rows[rowIndex].cells.entries()) {
      fn(cell, rowIndex, columnIndex, table);
    }
  },

  forCellInColumn: <TTableMetadata, TRowMetadata>(
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    columnIndex: number,
    fn: (...args: CellIterationArguments<TTableMetadata, TRowMetadata>) => void
  ): void => {
    for (const [rowIndex, row] of table.rows.entries()) {
      fn(row.cells[columnIndex], rowIndex, columnIndex, table);
    }
  },

  getCell: <TTableMetadata, TRowMetadata>(
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    rowIndex: number,
    columnIndex: number
  ): Cell | undefined => table.rows[rowIndex].cells[columnIndex],

  getCellsInColumn: <TTableMetadata, TRowMetadata>(
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    columnIndex: number
  ): Array<Cell> => table.rows.map((row) => row.cells[columnIndex]),

  /** Clears out the typed value and text of a particular cell */
  clearCell: <TTableMetadata, TRowMetadata>(
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    rowIndex: number,
    columnIndex: number
  ): void => {
    const cell = StructuredTableHelper.getCell(table, rowIndex, columnIndex);
    if (cell) {
      StructuredTableHelper._clearCell(cell);
    }
  },

  clearColumn: <TTableMetadata, TRowMetadata>(
    table: StructuredTable<TTableMetadata, TRowMetadata>,
    columnIndex: number
  ): void => {
    StructuredTableHelper.forCellInColumn(table, columnIndex, StructuredTableHelper._clearCell);
  },

  // PRIVATE FUNCTIONS

  _clearCell: (cell: Cell): void => {
    cell.text = '';
    cell.typedValue = undefined;
  },
};

export default StructuredTableHelper;
